import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { formatCohortRange } from "../lib/cohorts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = () => ({ name: "", email: "", pecb_student_id: "" });

export default function AttendeesForm() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [order, setOrder] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orders/${orderId}`, { params: { token } })
      .then((r) => {
        const o = r.data.order || r.data;
        setOrder(o);
        setRows(o.participants_details?.length ? o.participants_details : Array.from({ length: o.participants }, blank));
      })
      .catch(() => setError("This link is invalid or has expired."));
  }, [orderId, token]);

  const set = (i, k) => (e) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));

  const submit = async () => {
    if (rows.some((r) => !r.name.trim() || !/\S+@\S+\.\S+/.test(r.email))) return toast.error("Please enter a name and valid email for every attendee");
    setSaving(true);
    try {
      await axios.post(`${API}/orders/${orderId}/attendees`, { token, attendees: rows.map((r) => ({ ...r, pecb_student_id: r.pecb_student_id || null })) });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save attendees");
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="attendees-error">{error}</div>;
  if (!order) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  const submitted = done || !order.attendees_pending;

  return (
    <div className="min-h-screen bg-slate-50 py-14" data-testid="attendees-page">
      <title>Add Attendees | Compliance Management Systems</title>
      <meta name="robots" content="noindex" />
      <div className="container mx-auto px-4 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2"><Users className="h-5 w-5 text-brand" /> Attendee details</CardTitle>
            <p className="text-sm text-muted-foreground">
              {order.company_name} · {order.participants} × {order.course_title}
              {order.cohort ? ` · ${formatCohortRange(order.cohort)}` : ""}
              {order.proforma_number ? ` · Pro forma ${order.proforma_number}` : ""}
            </p>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-6 space-y-3" data-testid="attendees-submitted">
                <CheckCircle2 className="h-12 w-12 text-brand mx-auto" />
                <p className="font-heading font-semibold text-lg">Enrollment complete</p>
                <p className="text-sm text-muted-foreground">Each attendee has been emailed their course access details.</p>
                <ul className="text-sm text-left max-w-sm mx-auto pt-2">
                  {rows.map((r) => <li key={r.email} className="border-b py-1">{r.name} — {r.email}</li>)}
                </ul>
              </div>
            ) : (
              <div className="space-y-4">
                {rows.map((r, i) => (
                  <div key={i} className="grid sm:grid-cols-3 gap-2" data-testid={`attendee-row-${i}`}>
                    <Input placeholder={`Attendee ${i + 1} full name *`} value={r.name} onChange={set(i, "name")} data-testid={`attendee-name-${i}`} />
                    <Input type="email" placeholder="Email *" value={r.email} onChange={set(i, "email")} data-testid={`attendee-email-${i}`} />
                    <Input placeholder="PECB student ID (optional)" value={r.pecb_student_id || ""} onChange={set(i, "pecb_student_id")} data-testid={`attendee-pecb-${i}`} />
                  </div>
                ))}
                <Button onClick={submit} disabled={saving} className="w-full bg-brand hover:bg-brand-dark text-white" data-testid="attendees-submit-btn">
                  {saving ? "Saving…" : "Submit Attendees & Complete Enrollment"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
